import { Fragment, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { fetchTaskLogSummary, listTaskLogs } from '@src/api/taskLogger';
import { useMessage } from '@src/context/common';

import RunMessages from './components/RunMessages';

dayjs.extend(relativeTime);

const WINDOWS = [
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

const WINDOW_DAYS = { '24h': 1, '7d': 7, '30d': 30 };

const statusColorMap = { running: 'info', succeeded: 'success', failed: 'error' };

function relTime(value) {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.fromNow() : '—';
}

function StatusDot({ status }) {
  const color = statusColorMap[status] || 'default';
  return <Chip size="small" label={status || '—'} color={color} sx={{ textTransform: 'capitalize' }} />;
}

function SuccessBar({ rate }) {
  if (rate === null || rate === undefined) {
    return (
      <Typography variant="caption" color="text.secondary">
        no finished runs
      </Typography>
    );
  }
  const pct = Math.round(rate * 100);
  const color = pct >= 99 ? 'success' : pct >= 90 ? 'warning' : 'error';
  return (
    <Stack spacing={0.5} sx={{ minWidth: 110 }}>
      <Typography variant="caption">{pct}% success</Typography>
      <LinearProgress variant="determinate" value={pct} color={color} sx={{ height: 6, borderRadius: 3 }} />
    </Stack>
  );
}

function CountChips({ counts }) {
  return (
    <Stack direction="row" spacing={1}>
      <Tooltip title="Succeeded">
        <Chip size="small" variant="outlined" color="success" label={`✓ ${counts.succeeded}`} />
      </Tooltip>
      <Tooltip title="Failed">
        <Chip
          size="small"
          variant={counts.failed ? 'filled' : 'outlined'}
          color="error"
          label={`✗ ${counts.failed}`}
        />
      </Tooltip>
      {counts.running > 0 && (
        <Tooltip title="Running">
          <Chip size="small" variant="outlined" color="info" label={`⏳ ${counts.running}`} />
        </Tooltip>
      )}
    </Stack>
  );
}

export default function ByJobTab({ onDrillToRuns }) {
  const message = useMessage();
  const [window, setWindow] = useState('24h');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [expanded, setExpanded] = useState(null); // family key
  const [runsByFamily, setRunsByFamily] = useState({}); // family -> { loading, runs }
  const [expandedRun, setExpandedRun] = useState(null); // run id

  const load = async (win = window) => {
    setLoading(true);
    try {
      const res = await fetchTaskLogSummary({ window: win });
      setData(res?.data || null);
    } catch (error) {
      console.error('Error loading task summary', error);
      message?.error?.('Failed to load task summary');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(window);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window]);

  const families = useMemo(() => {
    const all = data?.families || [];
    return categoryFilter ? all.filter((f) => f.category === categoryFilter) : all;
  }, [data, categoryFilter]);

  const loadFamilyRuns = async (family) => {
    setRunsByFamily((prev) => ({ ...prev, [family]: { loading: true, runs: prev[family]?.runs || [] } }));
    const createdAfter = dayjs().subtract(WINDOW_DAYS[window], 'day').format('YYYY-MM-DD');
    try {
      const res = await listTaskLogs({
        search: family,
        created_after: createdAfter,
        page_size: 50,
        ordering: '-created',
      });
      setRunsByFamily((prev) => ({
        ...prev,
        [family]: { loading: false, runs: res?.data?.results || [] },
      }));
    } catch (error) {
      console.error('Error loading family runs', error);
      setRunsByFamily((prev) => ({ ...prev, [family]: { loading: false, runs: [] } }));
    }
  };

  const toggleFamily = (family) => {
    setExpandedRun(null);
    if (expanded === family) {
      setExpanded(null);
      return;
    }
    setExpanded(family);
    if (!runsByFamily[family]) loadFamilyRuns(family);
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={window}
          onChange={(_, val) => val && setWindow(val)}
        >
          {WINDOWS.map((w) => (
            <ToggleButton key={w.value} value={w.value}>
              {w.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={() => load(window)}>
          Refresh
        </Button>
      </Stack>

      {/* Category summary cards — click to filter the table below. */}
      <Grid container spacing={1.5}>
        {(data?.categories || []).map((cat) => {
          const active = categoryFilter === cat.category;
          const failing = cat.families_failing > 0;
          return (
            <Grid item xs={6} sm={4} md={2} key={cat.category}>
              <Card
                variant={active ? 'elevation' : 'outlined'}
                onClick={() => setCategoryFilter(active ? null : cat.category)}
                sx={{
                  cursor: 'pointer',
                  borderColor: failing ? 'error.main' : undefined,
                  bgcolor: active ? 'action.selected' : undefined,
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="subtitle2" noWrap>
                    {cat.label}
                  </Typography>
                  <Typography variant="h6" color={failing ? 'error.main' : 'text.primary'}>
                    {failing ? `${cat.families_failing} failing` : 'OK'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {cat.families} job{cat.families === 1 ? '' : 's'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {categoryFilter && (
        <Box>
          <Chip
            label={`Filtered: ${categoryFilter}`}
            onDelete={() => setCategoryFilter(null)}
            size="small"
          />
        </Box>
      )}

      <Card>
        <CardContent>
          {loading && !data ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={24} />
            </Stack>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={48} />
                    <TableCell>Job</TableCell>
                    <TableCell>Last run</TableCell>
                    <TableCell>Runs ({window})</TableCell>
                    <TableCell>Success</TableCell>
                    <TableCell>Last failure</TableCell>
                    <TableCell align="right">Avg dur.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {families.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No task runs in this window.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    families.map((fam) => {
                      const isOpen = expanded === fam.family;
                      const famRuns = runsByFamily[fam.family];
                      return (
                        <Fragment key={fam.family}>
                          <TableRow hover sx={{ bgcolor: fam.counts.failed ? 'rgba(211,47,47,0.06)' : undefined }}>
                            <TableCell>
                              <IconButton size="small" onClick={() => toggleFamily(fam.family)}>
                                {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="subtitle2">{fam.label}</Typography>
                                <Chip size="small" variant="outlined" label={fam.category_label} />
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {fam.family}
                                {fam.client_scoped && fam.clients_failed > 0
                                  ? ` · ${fam.clients_failed} client${fam.clients_failed === 1 ? '' : 's'} failing`
                                  : ''}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {fam.last_run ? (
                                <Stack spacing={0.5}>
                                  <StatusDot status={fam.last_run.status} />
                                  <Typography variant="caption" color="text.secondary">
                                    {relTime(fam.last_run.created)}
                                  </Typography>
                                </Stack>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>
                              <CountChips counts={fam.counts} />
                            </TableCell>
                            <TableCell>
                              <SuccessBar rate={fam.success_rate} />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              {fam.last_failure ? (
                                <Stack spacing={0.25}>
                                  <Typography variant="caption" color="error.main">
                                    {relTime(fam.last_failure.created)}
                                    {fam.last_failure.client_name ? ` · ${fam.last_failure.client_name}` : ''}
                                  </Typography>
                                  <Tooltip title={fam.last_failure.reason || ''}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        overflow: 'hidden',
                                        maxWidth: 300,
                                      }}
                                    >
                                      {fam.last_failure.reason || '(no message)'}
                                    </Typography>
                                  </Tooltip>
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  none
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {fam.avg_duration_seconds != null ? `${fam.avg_duration_seconds}s` : '—'}
                            </TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ bgcolor: (theme) => theme.palette.action.hover }}>
                                <Box sx={{ p: 1.5 }}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={1}
                                  >
                                    <Typography variant="subtitle2">Recent runs · {fam.label}</Typography>
                                    {onDrillToRuns && (
                                      <Link
                                        component="button"
                                        variant="caption"
                                        onClick={() => onDrillToRuns(fam.family)}
                                      >
                                        Open in All runs →
                                      </Link>
                                    )}
                                  </Stack>
                                  {!famRuns || famRuns.loading ? (
                                    <Stack alignItems="center" py={2}>
                                      <CircularProgress size={18} />
                                    </Stack>
                                  ) : famRuns.runs.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No runs found in this window.
                                    </Typography>
                                  ) : (
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell width={40} />
                                          <TableCell>Run</TableCell>
                                          <TableCell>Client</TableCell>
                                          <TableCell>Status</TableCell>
                                          <TableCell>Created</TableCell>
                                          <TableCell align="right">Dur.</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {famRuns.runs.map((run) => {
                                          const runOpen = expandedRun === run.id;
                                          return (
                                            <Fragment key={run.id}>
                                              <TableRow hover>
                                                <TableCell>
                                                  <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                      setExpandedRun((cur) => (cur === run.id ? null : run.id))
                                                    }
                                                  >
                                                    {runOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                                  </IconButton>
                                                </TableCell>
                                                <TableCell>
                                                  <Typography variant="caption" color="text.secondary">
                                                    {run.name} · #{run.id}
                                                  </Typography>
                                                </TableCell>
                                                <TableCell>
                                                  {run.client_name || run.parent_company_name || '—'}
                                                </TableCell>
                                                <TableCell>
                                                  <StatusDot status={run.status} />
                                                </TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                  {relTime(run.created)}
                                                </TableCell>
                                                <TableCell align="right">
                                                  {run.duration_seconds != null ? `${run.duration_seconds}s` : '—'}
                                                </TableCell>
                                              </TableRow>
                                              {runOpen && (
                                                <TableRow>
                                                  <TableCell colSpan={6}>
                                                    <Box sx={{ p: 1 }}>
                                                      <RunMessages taskId={run.id} />
                                                    </Box>
                                                  </TableCell>
                                                </TableRow>
                                              )}
                                            </Fragment>
                                          );
                                        })}
                                      </TableBody>
                                    </Table>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {data?.generated_at && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Updated {relTime(data.generated_at)} · cached ~60s
            </Typography>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
